import { Icon } from '@chakra-ui/react';
import { BasePagination, IconButtonWithTooltip, PlusIcon } from '@elkaso-app/web-design';
import { useGetCreditFacilitiesApi } from 'apis/bnpl';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  DateTimeCell,
  IDateTimeCell,
  TableCell,
  TableHeader,
} from 'components/base-table/components';
import { Can } from 'configs/can';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { BnplStatusBadge } from 'pages/bnpl/components/bnpl-status-badge';
import { CreateCreditFacilityModal } from 'pages/bnpl/components/create-credit-facility-modal';
import { formatAmount } from 'pages/bnpl/utils/bnpl-statuses';
import React, { useEffect } from 'react';
import { FiEye } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

export const CreditFacilitiesListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { isLoading, isFetching, data } = useGetCreditFacilitiesApi();

  const { pageCount, total, data: facilitiesData } = data ?? {};

  useEffect(() => {
    dispatch(changeTitle('BNPL - Credit Facilities'));
  }, []);

  interface IColumnsAccessorData {
    id: number;
    restaurant: { id: number; nameEn: string } | undefined;
    supplier: { id: number; name: string } | undefined;
    facilityStatus: string;
    creditLimit: number;
    utilizedAmount: number;
    availableAmount: number;
    createdAt: string;
    actions: any;
  }

  const columns = React.useMemo(
    () => [
      {
        Header: <TableHeader name='ID' />,
        accessor: 'id',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['id']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Restaurant' />,
        accessor: 'restaurant',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['restaurant']>) => (
          <TableCell value={value?.nameEn || '---'} fontWeight='semibold' color='gray.600' />
        ),
      },
      {
        Header: <TableHeader name='Supplier' />,
        accessor: 'supplier',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['supplier']>) => (
          <TableCell value={value?.name || '---'} />
        ),
      },
      {
        Header: <TableHeader name='Status' />,
        accessor: 'facilityStatus',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['facilityStatus']>) => (
          <BnplStatusBadge status={value} type='facility' />
        ),
      },
      {
        Header: <TableHeader name='Credit Limit' />,
        accessor: 'creditLimit',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['creditLimit']>) => (
          <TableCell value={formatAmount(value)} />
        ),
      },
      {
        Header: <TableHeader name='Utilized' />,
        accessor: 'utilizedAmount',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['utilizedAmount']>) => (
          <TableCell value={formatAmount(value)} />
        ),
      },
      {
        Header: <TableHeader name='Available' />,
        accessor: 'availableAmount',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['availableAmount']>) => (
          <TableCell value={formatAmount(value)} />
        ),
      },
      {
        Header: <TableHeader name='Created' />,
        accessor: 'createdAt',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['createdAt']>) => (
          <CustomTableCell<IDateTimeCell> as={DateTimeCell} date={value} />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <IconButtonWithTooltip
            size='sm'
            label='View Details'
            aria-label='view-details-btn'
            Icon={<Icon as={FiEye} />}
            variant='unstyled'
            onClick={() => history.push(`/app/bnpl/credit-facilities/${value?.id}`)}
          />
        ),
      },
    ],
    []
  );

  const columnsAccessorData = React.useMemo(
    () =>
      facilitiesData?.map((facility): IColumnsAccessorData => {
        return {
          id: facility?.id,
          restaurant: facility?.restaurant,
          supplier: facility?.supplier,
          facilityStatus: facility?.facilityStatus,
          creditLimit: facility?.creditLimit,
          utilizedAmount: facility?.utilizedAmount,
          availableAmount: facility?.availableAmount,
          createdAt: facility?.createdAt,
          actions: facility,
        };
      }),
    [facilitiesData]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='Credit Facilities' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <ClearFilterButton />

          <Can I='manage' a='CreditFacility'>
            <CreateCreditFacilityModal
              trigger={
                <IconButtonWithTooltip
                  label='Create credit facility'
                  aria-label='create-credit-facility-btn'
                  Icon={<PlusIcon />}
                />
              }
            />
          </Can>
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      <BaseTableContainer.TableWrapper>
        <BaseTable columns={columns} data={columnsAccessorData} />
      </BaseTableContainer.TableWrapper>

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};
